import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { pluginRegistry } from '../plugin-registry';
import { exportState, importState, loadState, updateState } from '../storage';
import type { DevToolsPlugin } from '../types';
import { styles } from './acme-dev-tools.styles';

@customElement('acme-dev-tools')
export class AcmeDevTools extends LitElement {
  static override styles = styles;

  @property({ type: Boolean, reflect: true }) open = false;

  @state() private activePluginId: string | null = null;
  @state() private plugins: DevToolsPlugin[] = [];
  @state() private _canScrollLeft = false;
  @state() private _canScrollRight = false;

  private _unsubscribe: (() => void) | null = null;
  private _onBadgeUpdate = () => this.requestUpdate();
  private _resizeObserver: ResizeObserver | null = null;

  // Drag state
  private _dragging = false;
  private _dragOffsetX = 0;
  private _dragOffsetY = 0;
  private _posX: number | null = null;
  private _posY: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    const stored = loadState();
    if (stored.panel) {
      this._posX = stored.panel.x;
      this._posY = stored.panel.y;
      this._applyPosition();
    }

    window.addEventListener('acme:devtools-badge-update', this._onBadgeUpdate);

    this._unsubscribe = pluginRegistry.onChange(() => {
      this.plugins = pluginRegistry.getAll();
      if (!this.activePluginId && this.plugins.length > 0) {
        this.activePluginId = this.plugins[0].id;
        this.plugins[0].onActivate?.();
      }
    });

    this.plugins = pluginRegistry.getAll();
    if (this.plugins.length > 0) {
      this.activePluginId = this.plugins[0].id;
      this.plugins[0].onActivate?.();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._unsubscribe?.();
    this._unsubscribe = null;
    window.removeEventListener('acme:devtools-badge-update', this._onBadgeUpdate);
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
  }

  protected override firstUpdated(): void {
    const tabBar = this.shadowRoot!.querySelector('.tab-bar') as HTMLElement;
    tabBar.addEventListener('scroll', this._updateScrollButtons, { passive: true });
    this._resizeObserver = new ResizeObserver(this._updateScrollButtons);
    this._resizeObserver.observe(tabBar);
  }

  protected override updated(_changedProperties: PropertyValues): void {
    if (_changedProperties.has('plugins')) {
      this._updateScrollButtons();
    }
  }

  private _updateScrollButtons = (): void => {
    const tabBar = this.shadowRoot?.querySelector('.tab-bar') as HTMLElement | null;
    if (!tabBar) {
      return;
    }
    this._canScrollLeft = tabBar.scrollLeft > 0;
    this._canScrollRight = tabBar.scrollLeft + tabBar.clientWidth < tabBar.scrollWidth - 1;
  };

  private _scrollTabBar(direction: 'left' | 'right'): void {
    const tabBar = this.shadowRoot?.querySelector('.tab-bar') as HTMLElement | null;
    if (!tabBar) {
      return;
    }
    tabBar.scrollBy({ left: direction === 'left' ? -120 : 120, behavior: 'smooth' });
  }

  private _selectTab(pluginId: string): void {
    const prev = this.plugins.find((p) => p.id === this.activePluginId);
    prev?.onDeactivate?.();
    const next = this.plugins.find((p) => p.id === pluginId);
    next?.onActivate?.();
    this.activePluginId = pluginId;
  }

  private _applyPosition(): void {
    if (this._posX !== null && this._posY !== null) {
      this.style.right = 'auto';
      this.style.bottom = 'auto';
      this.style.left = `${this._posX}px`;
      this.style.top = `${this._posY}px`;
    }
  }

  private _savePosition(): void {
    if (this._posX !== null && this._posY !== null) {
      updateState((s) => {
        s.panel = { x: this._posX!, y: this._posY! };
      });
    }
  }

  private _onTitleBarMouseDown = (event: MouseEvent): void => {
    this._dragging = true;
    const rect = this.getBoundingClientRect();
    this._dragOffsetX = event.clientX - rect.left;
    this._dragOffsetY = event.clientY - rect.top;
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
  };

  private _onMouseMove = (event: MouseEvent): void => {
    if (!this._dragging) {
      return;
    }
    this._posX = event.clientX - this._dragOffsetX;
    this._posY = event.clientY - this._dragOffsetY;
    this._applyPosition();
  };

  private _onMouseUp = (): void => {
    if (!this._dragging) {
      return;
    }
    this._dragging = false;
    this._savePosition();
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
  };

  private _close(): void {
    this.open = false;
  }

  private _exportConfig(): void {
    const json = exportState();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `acme-devtools-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private _importConfig(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = importState(reader.result as string);
        if (!result.success) {
          window.alert(`DevTools import failed: ${result.error}`);
          return;
        }
        window.dispatchEvent(new CustomEvent('acme:devtools-state-imported'));
        this.requestUpdate();
      };
      reader.readAsText(file);
    });
    input.click();
  }

  private _renderActivePlugin() {
    const plugin = this.plugins.find((p) => p.id === this.activePluginId);
    if (!plugin) {
      return nothing;
    }
    const tag = unsafeStatic(plugin.tagName);
    return staticHtml`<${tag}></${tag}>`;
  }

  render() {
    return html`
      <div class="title-bar" @mousedown=${this._onTitleBarMouseDown}>
        <div class="title-bar-left">
          <div class="title-bar-accent"></div>
          <span class="title-bar-label">ACME DevTools</span>
          <span class="title-bar-badge">b2b</span>
        </div>
        <div class="title-bar-actions">
          <button class="action-btn" title="Import config" @click=${() => this._importConfig()}>↑</button>
          <button class="action-btn" title="Export config" @click=${() => this._exportConfig()}>↓</button>
          <button class="close-btn" @click=${() => this._close()}>×</button>
        </div>
      </div>

      <div class="tab-bar-wrapper">
        ${this._canScrollLeft
          ? html`<button class="tab-scroll-btn" @click=${() => this._scrollTabBar('left')}>&#8249;</button>`
          : nothing}
        <div class="tab-bar">
          ${this.plugins.map((plugin) => {
            const badge = plugin.badge?.() ?? null;
            return html`
              <button
                class="tab ${this.activePluginId === plugin.id ? 'active' : ''}"
                @click=${() => this._selectTab(plugin.id)}>
                ${plugin.label} ${badge ? html`<span class="tab-badge" title=${badge}>⚠</span>` : nothing}
              </button>
            `;
          })}
        </div>
        ${this._canScrollRight
          ? html`<button class="tab-scroll-btn" @click=${() => this._scrollTabBar('right')}>&#8250;</button>`
          : nothing}
      </div>

      <div class="content">${this._renderActivePlugin()}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'acme-dev-tools': AcmeDevTools;
  }
}
